import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ENV from 'src/env.json';
import { requestClient, requestStatus } from 'src/lib/services/api/request-api';
import { useRequestQueryMutationDecorator } from 'src/features/requests/use-request-query-mutation-decorator';
import { useFormatter } from 'src/services/i18n/useFormatter';
import { useQueryClient } from '@tanstack/react-query';
import { authQueries } from 'src/api/queries';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { useToast } from 'src/components/ui/use-toast';
import { RenderForAdmin } from 'src/components/Middleware';
import { useApiErrorToast } from 'src/lib/utils/api-error-message';

type Props = {
  request: {
    id?: string;
    status: string;
    flow?: string;
    auto_approves_at?: string | null;
    archived_at?: string | null;
    files_delete_at?: string | null;
    files_deleted_at?: string | null;
  };
};

const DATE_FORMAT = { val: { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' } };

// Today's date in the tester's own timezone, as YYYY-MM-DD.
const localToday = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// Today means now (a midday time could still be in the future); past days use midday UTC.
const toDeliveredAt = (day: string): string =>
  day === localToday() ? new Date().toISOString() : `${day}T12:00:00Z`;

/**
 * File Retention, test environments only: set when a request in Ready for
 * review got there, so the 30-day auto-approve (and the "Auto-approves in N
 * days" label in Space) can be tested without waiting. The API refuses it on
 * live production; the control is hidden on production builds.
 */
type LoadedProps = { request: Props['request'] & { id: string } };

/**
 * Entry point used on the request and revisions pages. Hidden on production
 * builds, for non-admins, before the request has loaded, and on multi-outcome
 * parent projects (their outcomes carry the clocks, each on its own page).
 */
const RequestRetentionTestDate: React.FC<Props> = ({ request }) => {
  if (ENV.NODE_ENV === 'prod' || !request.id || request.flow === 'multi_outcome') {
    return null;
  }

  const loaded = { ...request, id: request.id };

  return (
    <RenderForAdmin>
      {request.status === requestStatus.complete ? (
        <RequestRetentionArchiveTest request={loaded} />
      ) : request.status === requestStatus.delivered ? (
        <RequestRetentionDeliveredTest request={loaded} />
      ) : null}
    </RenderForAdmin>
  );
};

const RequestRetentionDeliveredTest: React.FC<LoadedProps> = ({ request }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const showError = useApiErrorToast();
  const { formatDate } = useFormatter();
  const { invalidate } = useRequestQueryMutationDecorator(request.id);
  const queryClient = useQueryClient();
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await requestClient.setRetentionDeliveredAt(request.id, toDeliveredAt(date));
      await Promise.all([invalidate(), queryClient.invalidateQueries([authQueries.requests])]);
      setDate('');
      toast({ title: t('Success') || 'Success', description: t('Ready for review date updated') || '' });
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={'tw-my-2 tw-rounded-md tw-border tw-border-dashed tw-p-3'}>
      <div className={'tw-mb-2 tw-text-sm tw-font-medium'}>
        {t('Testing only: Ready for review since')}
      </div>
      <div className={'tw-flex tw-flex-wrap tw-items-center tw-gap-2'}>
        <Input
          type={'date'}
          value={date}
          max={localToday()}
          onChange={(e) => setDate(e.target.value)}
          className={'tw-h-10 tw-w-48'}
          disabled={saving}
        />
        <Button type={'button'} disabled={!date || saving} onClick={save}>
          {t('Set date')}
        </Button>
      </div>
      {request.auto_approves_at && (
        <div className={'tw-mt-1 tw-text-xs tw-text-muted-foreground'}>
          {`${t('Auto-approves on')} ${formatDate(request.auto_approves_at, DATE_FORMAT)}`}
        </div>
      )}
    </div>
  );
};

/**
 * File Retention, test environments only: archive an approved request now
 * instead of waiting 30 days. Once archived, shows the archive and delete dates.
 */
const RequestRetentionArchiveTest: React.FC<LoadedProps> = ({ request }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const showError = useApiErrorToast();
  const { formatDate } = useFormatter();
  const { invalidate } = useRequestQueryMutationDecorator(request.id);
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [deleteDate, setDeleteDate] = useState('');

  // Move the deletion date to test Space's 30- and 7-day warnings (#2772).
  const saveDeleteDate = async () => {
    setSaving(true);
    try {
      await requestClient.setRetentionFilesDeleteAt(request.id, deleteDate);
      await Promise.all([invalidate(), queryClient.invalidateQueries([authQueries.requests])]);
      setDeleteDate('');
      toast({ title: t('Success') || 'Success', description: t('File deletion date updated') || '' });
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const archiveNow = async () => {
    const question = t(
      'Archive this request now? Its videos are compressed and its files are scheduled for deletion. This cannot be undone.',
    );
    if (!window.confirm(question || '')) {
      return;
    }
    setSaving(true);
    try {
      await requestClient.archiveRetentionNow(request.id);
      await Promise.all([invalidate(), queryClient.invalidateQueries([authQueries.requests])]);
      toast({
        title: t('Success') || 'Success',
        description: t('Request archived, its videos are being compressed in the background') || '',
      });
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const deleteFilesNow = async () => {
    const question = t(
      "Delete this request's files now? Footage, delivered files and chat attachments are deleted (kept in the trash for 30 days, then removed for good). Clients can't get them back.",
    );
    if (!window.confirm(question || '')) {
      return;
    }
    setSaving(true);
    try {
      const result = await requestClient.deleteRetentionFilesNow(request.id);
      await Promise.all([invalidate(), queryClient.invalidateQueries([authQueries.requests])]);
      toast(
        result.files_deleted_at
          ? { title: t('Success') || 'Success', description: t("Request's files deleted") || '' }
          : {
              title: t('Not finished') || 'Not finished',
              description:
                t('Some files are still used by another request; they go once that request is due too') || '',
              variant: 'warning',
            },
      );
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={'tw-my-2 tw-rounded-md tw-border tw-border-dashed tw-p-3'}>
      <div className={'tw-mb-2 tw-text-sm tw-font-medium'}>{t('Testing only: file retention archive')}</div>
      {request.files_deleted_at ? (
        <div className={'tw-text-sm'}>
          {request.archived_at && `${t('Archived on')} ${formatDate(request.archived_at, DATE_FORMAT)} · `}
          {`${t('Files were deleted on')} ${formatDate(request.files_deleted_at, DATE_FORMAT)}`}
        </div>
      ) : request.archived_at ? (
        <>
          <div className={'tw-text-sm'}>
            {`${t('Archived on')} ${formatDate(request.archived_at, DATE_FORMAT)}`}
            {request.files_delete_at &&
              ` · ${t('Files will be deleted on')} ${formatDate(request.files_delete_at, DATE_FORMAT)}`}
          </div>
          <div className={'tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2'}>
            <span className={'tw-text-sm'}>{t('Change deletion date to')}</span>
            <Input
              type={'date'}
              value={deleteDate}
              // UTC, like the dates shown here and the API's check.
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setDeleteDate(e.target.value)}
              className={'tw-h-10 tw-w-48'}
              disabled={saving}
            />
            <Button type={'button'} disabled={!deleteDate || saving} onClick={saveDeleteDate}>
              {t('Set date')}
            </Button>
          </div>
          <Button type={'button'} variant={'outline'} className={'tw-mt-2'} disabled={saving} onClick={deleteFilesNow}>
            {t('Delete files now')}
          </Button>
        </>
      ) : (
        <>
          <Button type={'button'} disabled={saving} onClick={archiveNow}>
            {t('Archive now')}
          </Button>
          <div className={'tw-mt-1 tw-text-xs tw-text-muted-foreground'}>
            {t('Skips the waiting period after approval. The delete date is shown once archived')}
          </div>
        </>
      )}
    </div>
  );
};

export { RequestRetentionTestDate };
